import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { useToasts } from 'common/toasts/ToastsProvider'
import {
  useAcknowledgeAlertingIncident,
  useCloseAlertingIncident,
  useCompleteAlertingIncidentAction,
  useSkipAlertingIncidentAction,
} from 'modules/Monitoring/api/alerting/queries'
import { QueryKey } from 'modules/Monitoring/consts/query/queryKey'

export const useIncidentActions = () => {
  const queryClient = useQueryClient()
  const { addSuccessToast, addToast } = useToasts()

  const handleMutationSuccess = (action: string) => {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GetAlertingIncidentsTimelines],
    })
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GetQueuesHealthDetails],
    })
    addSuccessToast(`Incident ${action}`)
  }
  const handleMutationError = (error: unknown) => {
    if (isAxiosError(error) && error.response?.data.message) {
      addToast({
        label: error.response.data.message,
        icon: { name: 'Cross', size: 24, color: 'red' },
      })
    } else {
      captureException(error)
    }
  }

  const { mutateAsync: acknowledgeIncident, isPending: isAcknowledgePending } =
    useAcknowledgeAlertingIncident({
      onSuccess: () => handleMutationSuccess('acknowledged'),
      onError: handleMutationError,
    })
  const { mutateAsync: resolveIncident, isPending: isResolvePending } =
    useCloseAlertingIncident({
      onSuccess: () => handleMutationSuccess('resolved'),
      onError: handleMutationError,
    })
  const { mutateAsync: completeIncidentAction, isPending: isCompleteActionPending } =
    useCompleteAlertingIncidentAction({
      onSuccess: () => handleMutationSuccess('action completed'),
      onError: handleMutationError,
    })
  const { mutateAsync: skipIncidentAction, isPending: isSkipActionPending } =
    useSkipAlertingIncidentAction({
      onSuccess: () => handleMutationSuccess('action skipped'),
      onError: handleMutationError,
    })

  return {
    acknowledgeIncident,
    resolveIncident,

    completeIncidentAction,
    skipIncidentAction,

    isAcknowledgePending,
    isResolvePending,

    isCompleteActionPending,
    isSkipActionPending,
  }
}
